/** Governance in plain words (ADR-0066): agents, releases, rules, audit and evidence. */
export default {
  en: {
    cancel: "Cancel",
    issues: { reason_short: "Write a reason of at least 10 characters.", rule_name: "Give the rule a name in English and Arabic.", rule_system: "Choose the system.", rule_who: "Name at least one role, person or agent.", rule_days: "Choose at least one day.", rule_hours: "Start and end time must differ.", rule_condition: "Choose the value and what to compare it with.", rule_number: "More than / less than need a number.", rule_approvers: "Choose who approves.", rule_stricter: "This rule would change nothing: lower the level or keep agents off online models.", rule_limit: "Set a number of calls, an amount, or both." },
    purpose: "Which agents exist, who owns them, what they may do, and what is waiting for a decision.",
    tab: { agents: "Agents", releases: "Releases", rules: "Rules", evidence: "Audit and evidence" },
    technicalView: "Technical view",
    backToOverview: "Back to the plain view",
    env: { dev: "Development", test: "Testing", staging: "the final check", prod: "Live" },
    envTitle: { dev: "Development", test: "Testing", staging: "Final check", prod: "Live" },
    stop: {
      button: "Emergency stop…",
      title: "Emergency stop",
      intro: "Stops agents straight away. Use it when an agent is doing something harmful, or when a system it uses may be compromised.",
      one: "Stop one agent: it can't do anything until someone starts it again.",
      tenant: "Stop everything in your organisation: every agent stops at once.",
      global: "Stop the whole platform: platform administrators only, and a second one must confirm.",
      how: "You type the name of what you stop and a reason. Running requests can be cancelled at the same time. Everything is recorded in the audit trail.",
    },
    agents: {
      heading: "Agents",
      status: { running: "Running", paused: "Paused", retired: "Retired", waiting: "Waiting for sign-off", draft: "Being built" },
      owner: "Owner: <owner/>",
      version: "Version {{version}}",
      used_one: "Used once this week",
      used_other: "Used {{count}} times this week",
      usedNone: "Not used this week",
      canUse: "Can use: {{list}}",
      noTools: "no tools",
      noDocs: "no documents",
      docs: "documents in {{list}}",
      signsOff: "A person approves: {{list}}",
      stage: { label: "Release stage of {{agent}}", draft: "Draft", development: "Development", testing: "Testing", live: "Live", now: "now", done: "done" },
      next: {
        dev: "Request move to Development",
        test: "Request move to Testing",
        staging: "Request the final check",
        prod: "Request move to Live",
        builder: "Finish it in the Builder",
      },
      seeRules: "See its rules",
      pause: "Pause",
      stopMenu: {
        button: "Stop",
        aria: "Stop {{agent}}",
        pauseHelp: "It stops taking new requests. Its owners can resume it; nothing running is cancelled.",
        emergencyHelp: "Stops it at once and can cancel what is running. Needs a reason and is recorded.",
      },
      resume: "Resume",
      pauseAria: "Pause {{agent}}",
      resumeAria: "Resume {{agent}}",
      pauseTitle: "Pause {{agent}}?",
      pauseBody: "It stops taking new requests until someone resumes it. Give a short reason; it is recorded.",
      resumeTitle: "Resume {{agent}}?",
      resumeBody: "It takes requests again. Give a short reason; it is recorded.",
      paused: "{{agent}} is paused.",
      resumed: "{{agent}} is running again.",
      registry: "Registry details",
      identity: "Agent identity",
      empty: "No agents yet. Builders create them in the Builder.",
      error: "The agents could not be loaded.",
      search: "Find an agent",
      hideRetired: "Hide retired agents",
      noMatch: "No agent matches. Clear the search or show retired agents.",
      retiredNote: "Retired: it no longer takes requests.",
      rulesTitle: "Rules for {{agent}}",
      rule: {
        tools: "It can only use: {{list}}.",
        noTools: "It cannot use any tools; it only answers.",
        docs: "It can read these document collections: {{list}}.",
        data: "It works with data up to {{level}}.",
        approvals: "A person must approve before it does this: {{list}}.",
        humanSteps_one: "Its flow has a step where a person decides.",
        humanSteps_other: "Its flow has {{count}} steps where a person decides.",
        forPerson: "It only acts for the signed-in person who asks.",
        autonomous: "It may also run on its own, for example on a schedule.",
        tier: "Its risk level is {{tier}} of 4.",
        stop: "It can be paused here at any time, or stopped at once with Emergency stop.",
      },
      allRules: "All rules",
      aside: { rules: "Rules in plain words", allRules_one: "The rule", allRules_other: "All {{count}} rules", audit: "Audit records", auditLink: "Search records" },
    },
    pending: {
      asked: "<who/> asked to move {{agent}} {{version}} to <b>{{stage}}</b>.",
      needsOne: "It needs one sign-off from someone other than <who/>.",
      needsMany: "It needs {{count}} sign-offs from people other than <who/> ({{have}} so far).",
      you: "You can approve it.",
      yours: "You asked for this, so someone else must sign it off.",
      others: "A release approver will review it.",
      evals: "It must pass its tests first: {{suites}}.",
      evalsAny: "It must pass its tests first.",
      failed: "It was signed off, but the move itself did not finish. Use Try again on the Releases tab; if it fails again, ask your platform team.",
    },
    releases: {
      waiting: "Waiting for a decision",
      none: "Nothing is waiting for a sign-off.",
      history: "Earlier decisions",
      noHistory: "No earlier decisions yet.",
      done: "{{agent}} {{version}} moved to {{stage}} on {{date}}, signed off by <who/>.",
      doneNoSign: "{{agent}} {{version}} moved to {{stage}} on {{date}}; no sign-off was needed.",
      rejected: "<who/> turned down moving {{agent}} {{version}} to {{stage}} on {{date}}.",
      cancelled: "<who/> withdrew the request to move {{agent}} {{version}} to {{stage}}.",
      gatesTitle: "What each step needs",
      gate: "<b>{{stage}}:</b> {{signoffs}}{{tests}}",
      signoffs: {
        none: "no sign-off",
        one: "one sign-off from someone other than the person who asks",
        many: "{{count}} sign-offs from people other than the person who asks",
        highTier: " ({{count}} for high-risk agents)",
      },
      tests: "; it must pass: {{list}}",
      metricMin: "{{metric}} at least {{goal}}",
      metricMax: "{{metric}} at most {{goal}}",
      testsLink: "Open the agent's tests",
      qualityLink: "See the test results",
      tableLink: "All requests as a table",
      retry: "Try again",
      retryAria: "Try moving {{agent}} {{version}} again",
      retried: "{{agent}} {{version}}: the move was run again.",
      signOff: "Review and sign off",
      signOffAria: "Review and sign off {{agent}} {{version}}",
      withdraw: "Withdraw",
      withdrawAria: "Withdraw the request for {{agent}} {{version}}",
      withdrawn: "The request was withdrawn.",
      signTitle: "Sign off: {{agent}} {{version}} to {{stage}}",
      signBody: "Your decision is signed with your name and recorded. It cannot be changed afterwards.",
      because: "Reason given: {{reason}}",
      approve: "Approve",
      reject: "Turn down",
      recorded: "Your decision is recorded.",
      requestTitle: "Ask to move {{agent}} {{version}} to {{stage}}",
      requestBodyNone: "No sign-off is needed for this step; it happens straight away.",
      requestBody: "Someone other than you must sign it off.",
      requestReason: "Why now? (optional)",
      requestOk: "Send the request",
      requested: "Request sent.",
      kind: { model: "AI model:", tool: "Tool:", prompt: "Prompt:" },
      error: "The releases could not be loaded.",
    },
    orgRules: {
      title: "Your organisation's rules",
      intro: "Rules your organisation adds on top of the built-in ones. They can only make things stricter: refuse something, ask for an approval or set a limit. A second person approves every change before it applies.",
      builtinTitle: "Built-in rules (locked)",
      builtinIntro: "The platform's own rules. They apply to every organisation and change only through review by the platform team.",
      add: "Add a rule",
      none: "No rules yet. For example: \u201cPayments over 5,000 need a finance approver\u201d.",
      whoWrites: "Rules administrators and organisation administrators add and approve rules.",
      state: { draft: "Draft", pending: "Waiting for approval", live: "On", paused: "Paused", retired: "Retired" },
      version: "Version {{version}}",
      approvedBy: "approved by {{who}} on {{at}}",
      waiting: "A change (version {{version}}) is waiting for approval, by {{who}}.",
      waitingDraft: "A draft (version {{version}}) is not sent for approval yet.",
      waitingPause: "Pausing this rule is waiting for approval, by {{who}}.",
      waitingResume: "Turning this rule back on is waiting for approval, by {{who}}.",
      approve: "Approve",
      reject: "Reject",
      withdraw: "Withdraw",
      submit: "Send for approval",
      edit: "Change",
      pause: "Pause",
      resume: "Turn back on",
      history: "History",
      ownChange: "You made this change, so someone else approves it.",
      approved: "Rule approved. It applies from now on.",
      rejected: "Change rejected.",
      submitted: "Sent for approval.",
      withdrawn: "Withdrawn.",
      saved: "Draft saved.",
      changeRequested: "Sent for approval.",
      decisionNote: "Note (optional)",
      historyTitle: "History of \u201c{{name}}\u201d",
      restore: "Go back to this version",
      vstatus: { draft: "Draft", pending: "Waiting", live: "Live", superseded: "Replaced", rejected: "Rejected", withdrawn: "Withdrawn" },
      by: "by {{who}}",
      paused: "paused",
      editor: {
        titleNew: "Add a rule",
        titleEdit: "Change \u201c{{name}}\u201d",
        step: { kind: "What kind of rule", details: "Details", try: "Try it" },
        kind: {
          who_may_use: { title: "Who may use", text: "Only the people, roles or agents you name may use a system or one of its actions.", eg: "Only the treasury team may post payments." },
          when_allowed: { title: "When allowed", text: "A system or action only on certain days and hours.", eg: "Payments only Sunday to Thursday, 08:00 to 17:00." },
          needs_approval_if: { title: "Needs approval if", text: "A person must approve when a value of the call meets a condition.", eg: "Payments over 5,000 need a finance approver." },
          max_data_level: { title: "Never above a data level", text: "Agents never handle data above a level, and may be kept off online AI models.", eg: "HR agents never use online AI models." },
          limit_per_day: { title: "Limit per day", text: "At most a number of calls, or an amount, per person, agent or organisation each day.", eg: "At most 10 payments per person per day." },
        },
        nameEn: "Name (English)",
        nameAr: "Name (Arabic)",
        system: "System",
        actions: "Actions",
        allActions: "Every action",
        roles: "Roles",
        agents: "Agents",
        allAgents: "Every agent",
        people: "People (user IDs)",
        days: "Days",
        from: "From",
        to: "To",
        zone: "Time zone",
        condition: "When",
        always: "Always",
        field: "Value of the call",
        op: "Is",
        value: "Than / equal to",
        ops: { ">": "more than", ">=": "at least", "<": "less than", "<=": "at most", "==": "equal to", "!=": "not equal to" },
        approvers: "Approved by",
        level: "Highest data level",
        online: "Online AI models",
        onlineAllowed: "Allowed",
        onlineNever: "Never",
        systemOptional: "Only for this system (optional)",
        per: "Per",
        perOpt: { person: "person", agent: "agent", organisation: "whole organisation" },
        maxCalls: "At most this many calls",
        maxAmount: "At most this amount",
        amountField: "Amount from",
        note: "Why (optional)",
        tryIntro: "See what this rule would have done to the last 7 days of requests before anyone approves it.",
        tryRun: "Try it on last week",
        tryRefused: "Would have refused {{count}} of {{total}} requests.",
        tryApproval: "Would have asked for approval on {{count}} of {{total}} requests.",
        tryNone: "Would not have changed any of the {{total}} requests.",
        tryUnknown: "{{count}} couldn't be checked (their records are older than this kind of rule).",
        tryExample: "{{effect}} · {{when}} · {{what}}",
        effect: { refused: "Refused", approval: "Approval" },
        saveDraft: "Save draft",
        saveSubmit: "Save and send for approval",
      },
      text: {
        who_may_use: "Only {{who}} may use {{what}}.",
        when_allowed: "{{what}} only on {{days}}, {{from}} to {{to}} ({{zone}}).",
        needs_approval_if: "{{what}} needs approval by {{roles}} when {{condition}}.",
        needs_approval_always: "{{what}} always needs approval by {{roles}}.",
        max_data_level: "{{agents}}: no data above {{level}}{{online}}.",
        noOnline: ", and no online AI models",
        limit_per_day: "{{what}}: at most {{limit}} per {{per}} each day.",
        limitCalls: "{{count}} calls",
        limitAmount: "an amount of {{amount}}",
        and: " and ",
        what: "{{system}} ({{actions}})",
        whatAll: "{{system}}",
        everyAgent: "Every agent",
        condition: "{{field}} is {{op}} {{value}}",
        list: ", ",
      },
    },
    goBack: {
      button: "Go back to an earlier version…",
      aria: "Go back to an earlier version of {{agent}}",
      pickTitle: "Go back to an earlier version of {{agent}}",
      pickBody: "Choose the stage and the earlier version. It is released again in that stage, with the same sign-off as any release. Nothing is deleted: the newer version stays saved and can be released again later.",
      pickLabel: "Stage and version",
      option: "{{stage}}: version {{version}} (now {{current}})",
      next: "Next",
      title: "Go back to {{agent}} {{version}} in {{stage}}",
      explain: "Version {{version}} replaces version {{from}} in {{stage}}. New requests there use {{version}}; requests already running finish on {{from}}.",
      ok: "Go back",
      defaultReason: "Go back to an earlier version",
      done: "Version {{version}} is back in {{stage}}.",
    },
    rules: {
      intro: "Every rule the platform enforces, in plain words. Each one is checked on every action, before anything happens.",
      search: "Find a rule",
      count_one: "{{count}} rule",
      count_other: "{{count}} rules",
      controlled: "Controlled by:",
      always: "Always on",
      showCode: "Show the policy code",
      hideCode: "Hide the policy code",
      codeMissing: "The policy code is not available here.",
      codeWhat: "What this code checks",
      codeChanged: "Changed only through code review by the platform engineers, never from this screen.",
      codeSettings: "The setting that affects it:",
      codeSettingsNone: "No setting affects it: it is always on.",
      codeLines: "{{file}}, lines {{from}}–{{to}}",
      codeWhole: "Show the whole file",
      codePart: "Show only this rule",
      codeLabel: "Policy code for: {{rule}}",
      codeExplain: {
        four_eyes: "When someone decides a case task, the code compares the decider with the person who made the request. If they are the same person, the decision is refused.",
        human_decides: "Deciding or assigning a case task is refused unless a signed-in person does it directly. An agent, or a person acting through an agent, is refused.",
        task_roles: "Your organisation can list which roles may decide each kind of task. The code refuses a decision when the person holds none of the roles listed for that kind.",
        release_four_eyes: "When a release is signed off, the code compares the approver with the person who asked for it. The same person can never approve their own release.",
        live_two: "For agents of risk level 3 or higher, the code counts the distinct people who signed off. Going Live needs two of them; lower-risk agents need one.",
        eval_gate: "A release to the final check or Live is refused unless the version's test results passed. The results come from Quality.",
        signed_release: "A release to Live is refused unless the version carries a valid digital signature, which proves it is exactly the version that was tested.",
        approval_amount: "For each action, the code looks up the amount limit for the agent's risk level (platform setting, tightened by your organisation). If the action's amount is above it, the action waits for a person's approval.",
        approval_confidence: "If the AI reports how sure it is and that is below the limit for the agent's risk level, the action waits for a person's approval.",
        approval_tier: "Every action that changes data, by an agent at or above the configured risk level, is held for a person's approval.",
        untrusted: "If content from outside (an e-mail, a web page, a document) was part of what led to the action, the action is held for a person's approval.",
        always_approve: "Tool actions marked \"always needs approval\" in the tool's description are held for a person's approval, whatever the amount.",
        tools_granted: "The code checks the tool and the action against the list the agent was given in the Builder. Anything not on that list is refused.",
        tool_roles: "Some tool actions name roles the person asking must hold. If the person holds none of them, the action is refused.",
        dry_run: "In a test run, any action marked as changing data is refused, so tests never change your systems.",
        pii_hidden: "Fields the tool marks as personal data are listed for hiding unless the person holds the personal-data reader role.",
        for_person: "An agent may act without a signed-in person only if it is marked to run on its own (for example on a schedule). Otherwise the action is refused.",
        registered: "The agent must be in the registry, active, and approved for the stage it runs in (Development, Testing, Final check or Live).",
        deny_default: "The final decision is \"allowed\" only when the action is known and no rule refused it. Anything else is refused.",
        clearance: "Each piece of data has a level (Public, Internal, Confidential, Restricted). The action is refused if the data's level is above the person's clearance.",
        agent_ceiling: "Each agent has a highest data level, set when it is built. The action is refused if the data's level is above it.",
        tenant_ceiling: "If your organisation set a highest data level, any action with data above it is refused.",
        kill: "Before anything else, the code checks the emergency stops for the whole platform, your organisation and the agent. If one is on, the action is refused.",
        runner_groups: "If an agent is limited to certain groups of people, the code checks the person's groups and refuses everyone else.",
        memory_consent: "Writing a memory about a person is refused unless that person agreed. Erasing is always allowed for the person and for privacy officers.",
        retrieval_rows: "Knowledge searches return at most the configured number of rows, and only rows of your organisation and department.",
        confidential_stays: "Data above the online limit (Internal by default) is refused for AI models outside your servers. Such requests use the local models.",
        online: "AI models outside your servers are refused if your organisation switched them off, or if the model's address is not on the approved list.",
        model_approved: "An AI model is refused in a stage until it was tested and approved for that stage.",
        model_residency: "An AI model outside your servers is refused if its region or its provider is not on your organisation's allowed list.",
        kill_who: "Starting or releasing an emergency stop needs the emergency-stop operator or platform administrator role. Stopping the whole platform needs a platform administrator.",
        channel_consent: "An outgoing message is refused without the person's consent for that channel and purpose, if they opted out, during quiet hours (except urgent ones) or above the sending limits.",
        channel_handover: "Answering a conversation handed over by an agent needs a signed-in person with the channel agent role, and hand-over must be switched on for your organisation.",
        users_admin: "Managing users needs the organisation administrator (or platform administrator) role. Nobody may change their own roles or disable their own account.",
        users_platform: "Platform roles, and users of the Platform team, can only be managed by a platform administrator.",
        marketplace_cert: "Installing a component from outside the company is refused unless it is certified and its licence is open source. Certifying needs the marketplace curator role.",
        marketplace_four_eyes: "The person who published a listing can never certify it.",
        directory: "Looking up names of people and agents is limited to your own organisation.",
        agent_roles: "Only organisation administrators or agent administrators may let an agent do case work on its own (open or update cases); approving and administration always stay with people.",
        insights: "Process maps can only be read by the listed roles (builders, operators, administrators, auditors, …). Another organisation's maps need the platform auditor role.",
        activity: "Everyone sees their own requests. The list of everyone's requests needs an operator, organisation administrator or auditor role; another organisation needs the platform auditor role.",
        explain: "A request's story is refused unless you took part in it or hold a reviewer, operator, auditor or administrator role. The signed records need an auditor or administrator role.",
        agent_test: "Trying agents is refused outside Development, and for anyone without the builder or agent administrator role.",
        connect_system: "Connecting company systems needs the tool or platform administrator role in the Platform team. A connection test that would change data is refused.",
        platform_tools: "A file an agent makes is refused below the agent's own data level. A message by e-mail, chat app or SMS from an agent that sees data above Internal waits for an approval, and so does an agent closing a case.",
        org_rules: "The code reads your organisation's own rules (published after a second administrator approved them) and applies each one on top of the platform's: it refuses use by anyone the rule does not name, refuses outside the allowed hours, or asks for an extra approval. It can only make things stricter.",
      },
      none: "No rule matches.",
      topic: {
        people: "Who may decide",
        approvals: "Approvals and sign-offs",
        data: "Your data",
        agents: "Agents and tools",
        models: "AI models",
        stop: "Emergency stop",
        channels: "Messages to customers",
        admin: "People and roles",
        reading: "Who can see what",
      },
      link: {
        platform: "Platform settings",
        tenant: "Organisation settings",
        users: "Users and roles",
        killswitch: "Emergency stop",
        quality: "Quality",
        builder: "Builder",
        channels: "Channels",
        marketplace: "Marketplace",
        releases: "Releases",
      },
      level: { public: "Public", internal: "Internal", confidential: "Confidential", restricted: "Restricted" },
      every: "every amount",
      stoppedNone: "nothing is stopped right now",
      stopped_one: "{{count}} stop is on right now",
      stopped_other: "{{count}} stops are on right now",
      item: {
        four_eyes: "The person who asks can never approve their own request.",
        human_decides: "Approvals and decisions on cases are made by people, never by an agent.",
        task_roles: "Some kinds of task can only be decided by people with a particular role.",
        release_four_eyes: "Nobody can sign off their own release: someone other than the person who asked must approve it.",
        live_two: "Before a high-risk agent goes Live, two different people must sign it off.",
        eval_gate: "An agent, prompt or AI model reaches the final check or Live only after passing its tests.",
        signed_release: "Only signed versions can go Live.",
        approval_amount: "Payments and other actions above {{high}} need a person's approval for high-risk agents ({{medium}} for medium-risk agents; {{critical}} for critical agents).",
        approval_confidence: "When the AI is less than {{confidence}} sure, a person approves the action first.",
        approval_tier: "Every action that changes something, by an agent of risk level {{tier}} or higher, needs a person's approval.",
        untrusted: "If outside content (an e-mail, a web page, a document) influenced an action, a person approves it first.",
        always_approve: "Some tool actions always need a person's approval, whatever the amount.",
        tools_granted: "An agent can only use the tools it was given, and only the actions listed for it.",
        tool_roles: "Some tool actions also need the person asking to hold a particular role.",
        pii_hidden: "Personal data in tool results is hidden from people who may not see personal data.",
        dry_run: "A test run never changes anything in your systems.",
        confidential_stays: "Confidential data never leaves your servers: only data up to {{online}} may be sent to an AI model outside them.",
        online_off: "AI models outside your servers are switched off for your organisation.",
        online_on: "AI models outside your servers may be used, only through the approved list of addresses.",
        model_approved: "An AI model can only be used in a stage after it was tested and approved for it.",
        model_residency: "AI models must run in the regions and come from the providers your organisation allows.",
        clearance: "A person only sees data up to their clearance level.",
        agent_ceiling: "Each agent works with data only up to its own limit, set when it is built.",
        tenant_ceiling: "Data above {{level}} is never used in your organisation.",
        tenant_ceiling_none: "Your organisation has no extra data limit beyond each person's clearance and each agent's limit.",
        kill: "An emergency stop blocks every action of the stopped agent, your organisation or the whole platform at once — {{state}}.",
        kill_who: "Only emergency-stop operators and platform administrators can stop or restart; stopping the whole platform needs a platform administrator.",
        for_person: "Agents act for the signed-in person who asks; only agents marked to run on their own (for example on a schedule) may act without one.",
        registered: "Only registered, active agents approved for this stage can act.",
        deny_default: "Anything that is not explicitly allowed is refused.",
        runner_groups: "Some agents can only be used by people in particular groups.",
        memory_consent: "An agent remembers things about you only with your consent, and you can erase them at any time.",
        retrieval_rows: "A knowledge search returns at most {{rows}} rows at a time, and only rows you may see.",
        channel_consent: "Messages are only sent to people who agreed to receive them, outside quiet hours and within the sending limits.",
        channel_handover: "A conversation handed over by an agent is answered by a person with the channel agent role.",
        marketplace_cert: "Third-party components must be certified and have an open-source licence before anyone can install them.",
        marketplace_four_eyes: "Whoever publishes a listing cannot certify it.",
        users_admin: "Only organisation administrators manage users and roles, and nobody can change their own roles or disable themselves.",
        users_platform: "Platform roles are granted only by a platform administrator.",
        directory: "Anyone signed in can see the names of people and agents in their own organisation, never in another one.",
        agent_roles: "An agent does case work on its own only when an administrator of its organisation allows it; every change is recorded.",
        insights: "Process maps are for people who build, run, oversee or audit agents.",
        activity: "You always see your own requests; everyone's requests are visible to operators, organisation administrators and auditors.",
        explain: "The story of a request is shown to the person who asked, the people involved, reviewers, operators, auditors and organisation administrators; the signed records only to auditors and administrators.",
        agent_test: "Agents are tried and tested only in development, by builders and agent administrators; their actions are simulated unless the builder lets them really act.",
        connect_system: "Only tool and platform administrators connect company systems; a test can read for real but only simulates changes, and sign-in details stay in the secret store.",
        platform_tools: "Files agents make keep the agent's data level and open only for people cleared for it; messages that leave the platform from agents that see data above Internal, and agents closing a case, wait for a person's approval.",
        org_rules: "An organisation can add its own rules on top of the platform's, never looser: who may use something, when, how much data, online models, daily limits or an extra approval. A change needs a second administrator to approve it.",
      },
    },
    evidence: {
      headline_one: "{{n}} record this week.",
      headline_other: "{{n}} records this week.",
      checking: "Checking every record…",
      ok: "All checked — nothing has been changed or removed.",
      broken: "Warning: some records do not match. The audit trail may have been changed.",
      notChecked: "Not checked yet.",
      check: "Check again",
      export: "Export evidence…",
      advanced: "Advanced search",
      content: {
        title: "Keep conversation content as evidence",
        help: "Off unless your organisation turns it on. When on, the platform keeps what each request sent to the AI and answered, and what tools were sent and returned — with personal data masked and encrypted with your organisation's key — for the days you choose. Only auditors can read it, every read is recorded, and a person's erasure deletes theirs.",
        off: "Off — conversation content is not kept",
        on: "On — kept for {{days}} days",
        changedBy: "Last changed by",
        whoDecides: "Auditors and organisation administrators decide this.",
        turnOn: "Turn on",
        turnOff: "Turn off",
        turnOnTitle: "Keep conversation content as evidence",
        turnOffTitle: "Stop keeping conversation content",
        onExplain: "From now on, the content of your organisation's requests is kept, masked and encrypted, for the days below. Content already kept by earlier choices keeps its own end date.",
        offExplain: "New requests are no longer kept. What was kept stays until its end date, unless a person's erasure removes it.",
        days: "Keep for (days)",
        daysHelp: "1 to {{max}} days.",
        reason: "Why (recorded in the audit trail)",
        turnedOn: "Conversation content is now kept as evidence",
        turnedOff: "Conversation content is no longer kept",
      },
      licences: {
        title: "Licences of the platform's parts",
        help: "Every open-source part the platform is built from, with its licence and whether the licence policy allows it. Auditors attach this to evidence.",
        open: "Show the licence register",
      },
      search: {
        title: "Find records",
        type: "What happened",
        anyType: "Anything",
        person: "Person",
        agent: "Agent",
        case: "Case",
        run: "Request",
        period: "When",
        go: "Search",
        clear: "Clear",
      },
      period: { d7: "Last 7 days", d30: "Last 30 days", all: "Any time" },
      records: "Records",
      none: "No records match.",
      error: "The records could not be loaded.",
      sentence: {
        run: "<actor/> started a request",
        tool_call: "<actor/> used {{what}}",
        policy_allow: "The rules allowed {{what}} for <actor/>",
        policy_deny: "The rules refused {{what}} for <actor/>",
        approval_requested: "<actor/> asked a person to approve",
        approval_decided: "<actor/> decided an approval: {{what}}",
        config_change: "<actor/> changed a setting",
        kill_switch: "<actor/> used the emergency stop: {{what}}",
        promotion: "<actor/> worked on a release: {{what}}",
        memory_erase: "<actor/> erased remembered data",
        agent_lifecycle: "<actor/> changed an agent: {{what}}",
        security: "<actor/> looked at sensitive records",
        other: "<actor/>: {{what}}",
      },
      nobody: "The platform",
      code: {
        case_search: "searching cases",
        case_write: "changing a case",
        case_read: "reading a case",
        case: "Worked on a case",
        directory_read: "looking up people's names",
        promotion_request: "asking for a release",
        promotion_approve: "signing off a release",
        killswitch_engage: "using the emergency stop",
        killswitch_release: "lifting the emergency stop",
        memory_read: "Read what it remembers",
        memory_write: "Saved something to remember",
        memory_erase: "Erased remembered data",
        episodic: "Remembered a past conversation",
        model_call: "Asked an AI model",
        ingest: "Added documents to a knowledge collection",
        approval: "Asked a person to approve",
        approve: "approved",
        reject: "turned down",
        requested: "requested",
        approved: "approved",
        completed: "completed",
        completion_failed: "the move did not finish",
        cancelled: "withdrawn",
        rejected: "turned down",
        registered: "registered",
        pending_attestation: "waiting for its identity to be confirmed",
        attested: "identity confirmed",
        active: "running",
        suspended: "paused",
        retired: "retired",
        engage: "stopped",
        release: "started again",
        tenant: "your organisation",
        global: "the whole platform",
        agent: "one agent",
      },
      openRequest: "Open the request",
      record: "Record {{seq}}",
    },
  },
  ar: {
    cancel: "إلغاء",
    issues: { reason_short: "اكتب سببًا من 10 أحرف على الأقل.", rule_name: "أعطِ القاعدة اسمًا بالإنجليزية والعربية.", rule_system: "اختر النظام.", rule_who: "حدّد دورًا أو شخصًا أو وكيلًا واحدًا على الأقل.", rule_days: "اختر يومًا واحدًا على الأقل.", rule_hours: "يجب أن يختلف وقت البدء عن وقت الانتهاء.", rule_condition: "اختر القيمة وما تُقارن به.", rule_number: "أكثر من / أقل من تحتاج رقمًا.", rule_approvers: "اختر من يعتمد.", rule_stricter: "هذه القاعدة لن تغيّر شيئًا: اخفض المستوى أو امنع الوكلاء من النماذج عبر الإنترنت.", rule_limit: "حدّد عددًا من الطلبات أو مبلغًا أو كليهما." },
    purpose: "ما الوكلاء الموجودون، ومن يملكهم، وما المسموح لهم، وما الذي ينتظر قرارًا.",
    tab: { agents: "الوكلاء", releases: "الإصدارات", rules: "القواعد", evidence: "التدقيق والأدلة" },
    technicalView: "العرض التقني",
    backToOverview: "العودة إلى العرض المبسّط",
    env: { dev: "التطوير", test: "الاختبار", staging: "الفحص النهائي", prod: "التشغيل الفعلي" },
    envTitle: { dev: "التطوير", test: "الاختبار", staging: "الفحص النهائي", prod: "التشغيل الفعلي" },
    stop: {
      button: "إيقاف طارئ…",
      title: "الإيقاف الطارئ",
      intro: "يوقف الوكلاء فورًا. استخدمه عندما يقوم وكيل بشيء ضار، أو عندما يُحتمل اختراق نظام يستخدمه.",
      one: "إيقاف وكيل واحد: لا يستطيع فعل أي شيء حتى يعيد أحدهم تشغيله.",
      tenant: "إيقاف كل شيء في مؤسستك: تتوقف جميع الوكلاء دفعة واحدة.",
      global: "إيقاف المنصة كلها: لمسؤولي المنصة فقط، ويجب أن يؤكد مسؤول ثانٍ.",
      how: "تكتب اسم ما توقفه وسببًا. يمكن إلغاء الطلبات الجارية في الوقت نفسه. ويُسجَّل كل شيء في سجل التدقيق.",
    },
    agents: {
      heading: "الوكلاء",
      status: { running: "يعمل", paused: "متوقف مؤقتًا", retired: "متقاعد", waiting: "بانتظار الاعتماد", draft: "قيد البناء" },
      owner: "المالك: <owner/>",
      version: "الإصدار {{version}}",
      used_zero: "لم يُستخدم هذا الأسبوع",
      used_one: "استُخدم مرة واحدة هذا الأسبوع",
      used_two: "استُخدم مرتين هذا الأسبوع",
      used_few: "استُخدم {{count}} مرات هذا الأسبوع",
      used_many: "استُخدم {{count}} مرة هذا الأسبوع",
      used_other: "استُخدم {{count}} مرة هذا الأسبوع",
      usedNone: "لم يُستخدم هذا الأسبوع",
      canUse: "يمكنه استخدام: {{list}}",
      noTools: "لا أدوات",
      noDocs: "لا مستندات",
      docs: "مستندات في {{list}}",
      signsOff: "يعتمد شخصٌ: {{list}}",
      stage: { label: "مرحلة إصدار {{agent}}", draft: "مسودة", development: "التطوير", testing: "الاختبار", live: "التشغيل الفعلي", now: "الآن", done: "مكتمل" },
      next: {
        dev: "طلب النقل إلى التطوير",
        test: "طلب النقل إلى الاختبار",
        staging: "طلب الفحص النهائي",
        prod: "طلب النقل إلى التشغيل الفعلي",
        builder: "أكمله في المنشئ",
      },
      seeRules: "عرض قواعده",
      pause: "إيقاف مؤقت",
      stopMenu: {
        button: "إيقاف",
        aria: "إيقاف {{agent}}",
        pauseHelp: "يتوقف عن استقبال طلبات جديدة. يستطيع مالكوه استئنافه، ولا يُلغى ما يعمل الآن.",
        emergencyHelp: "يوقفه فورًا ويمكن أن يلغي ما يعمل الآن. يحتاج إلى سبب ويُسجَّل.",
      },
      resume: "استئناف",
      pauseAria: "إيقاف {{agent}} مؤقتًا",
      resumeAria: "استئناف {{agent}}",
      pauseTitle: "إيقاف {{agent}} مؤقتًا؟",
      pauseBody: "يتوقف عن استقبال الطلبات الجديدة حتى يستأنفه أحدهم. اكتب سببًا مختصرًا؛ فهو يُسجَّل.",
      resumeTitle: "استئناف {{agent}}؟",
      resumeBody: "يستقبل الطلبات من جديد. اكتب سببًا مختصرًا؛ فهو يُسجَّل.",
      paused: "تم إيقاف {{agent}} مؤقتًا.",
      resumed: "عاد {{agent}} إلى العمل.",
      registry: "تفاصيل السجل",
      identity: "هوية الوكيل",
      empty: "لا يوجد وكلاء بعد. ينشئهم المطوّرون في المنشئ.",
      error: "تعذّر تحميل الوكلاء.",
      search: "ابحث عن وكيل",
      hideRetired: "إخفاء الوكلاء المتقاعدين",
      noMatch: "لا يوجد وكيل مطابق. امسح البحث أو اعرض الوكلاء المتقاعدين.",
      retiredNote: "متقاعد: لم يعد يستقبل طلبات.",
      rulesTitle: "قواعد {{agent}}",
      rule: {
        tools: "لا يستخدم إلا: {{list}}.",
        noTools: "لا يستخدم أي أدوات؛ يجيب فقط.",
        docs: "يمكنه قراءة مجموعات المستندات هذه: {{list}}.",
        data: "يعمل مع بيانات حتى مستوى {{level}}.",
        approvals: "يجب أن يعتمد شخصٌ قبل أن يفعل هذا: {{list}}.",
        humanSteps_zero: "لا توجد في مساره خطوة يقرر فيها شخص.",
        humanSteps_one: "في مساره خطوة يقرر فيها شخص.",
        humanSteps_two: "في مساره خطوتان يقرر فيهما شخص.",
        humanSteps_few: "في مساره {{count}} خطوات يقرر فيها شخص.",
        humanSteps_many: "في مساره {{count}} خطوة يقرر فيها شخص.",
        humanSteps_other: "في مساره {{count}} خطوة يقرر فيها شخص.",
        forPerson: "لا يعمل إلا نيابةً عن الشخص المسجّل الذي يطلب.",
        autonomous: "يمكنه أيضًا العمل وحده، مثلًا وفق جدول زمني.",
        tier: "مستوى خطورته {{tier}} من 4.",
        stop: "يمكن إيقافه مؤقتًا من هنا في أي وقت، أو إيقافه فورًا بالإيقاف الطارئ.",
      },
      allRules: "كل القواعد",
      aside: {
        rules: "القواعد بكلمات بسيطة",
        allRules_zero: "لا توجد قواعد",
        allRules_one: "القاعدة",
        allRules_two: "القاعدتان",
        allRules_few: "كل القواعد الـ{{count}}",
        allRules_many: "كل القواعد الـ{{count}}",
        allRules_other: "كل القواعد الـ{{count}}",
        audit: "سجلات التدقيق",
        auditLink: "البحث في السجلات",
      },
    },
    pending: {
      asked: "طلب <who/> نقل {{agent}} {{version}} إلى <b>{{stage}}</b>.",
      needsOne: "يحتاج إلى اعتماد واحد من شخص غير <who/>.",
      needsMany: "يحتاج إلى {{count}} اعتمادات من أشخاص غير <who/> ({{have}} حتى الآن).",
      you: "يمكنك اعتماده.",
      yours: "أنت من طلب ذلك، لذا يجب أن يعتمده شخص آخر.",
      others: "سيراجعه أحد معتمدي الإصدارات.",
      evals: "يجب أن يجتاز اختباراته أولًا: {{suites}}.",
      evalsAny: "يجب أن يجتاز اختباراته أولًا.",
      failed: "تم الاعتماد، لكن النقل نفسه لم يكتمل. استخدم «إعادة المحاولة» في تبويب الإصدارات؛ وإن فشل مجددًا فاطلب المساعدة من فريق المنصة.",
    },
    releases: {
      waiting: "بانتظار قرار",
      none: "لا شيء ينتظر اعتمادًا.",
      history: "القرارات السابقة",
      noHistory: "لا توجد قرارات سابقة بعد.",
      done: "انتقل {{agent}} {{version}} إلى {{stage}} في {{date}}، باعتماد <who/>.",
      doneNoSign: "انتقل {{agent}} {{version}} إلى {{stage}} في {{date}}؛ لم يلزم اعتماد.",
      rejected: "رفض <who/> نقل {{agent}} {{version}} إلى {{stage}} في {{date}}.",
      cancelled: "سحب <who/> طلب نقل {{agent}} {{version}} إلى {{stage}}.",
      gatesTitle: "ما تحتاجه كل خطوة",
      gate: "<b>{{stage}}:</b> {{signoffs}}{{tests}}",
      signoffs: {
        none: "بلا اعتماد",
        one: "اعتماد واحد من شخص غير الطالب",
        many: "{{count}} اعتمادات من أشخاص غير الطالب",
        highTier: " ({{count}} للوكلاء عالية الخطورة)",
      },
      tests: "؛ ويجب أن يجتاز: {{list}}",
      metricMin: "{{metric}} {{goal}} على الأقل",
      metricMax: "{{metric}} {{goal}} على الأكثر",
      testsLink: "فتح اختبارات الوكيل",
      qualityLink: "عرض نتائج الاختبارات",
      tableLink: "كل الطلبات في جدول",
      retry: "إعادة المحاولة",
      retryAria: "إعادة محاولة نقل {{agent}} {{version}}",
      retried: "{{agent}} {{version}}: أُعيد تنفيذ النقل.",
      signOff: "المراجعة والاعتماد",
      signOffAria: "مراجعة واعتماد {{agent}} {{version}}",
      withdraw: "سحب",
      withdrawAria: "سحب طلب {{agent}} {{version}}",
      withdrawn: "تم سحب الطلب.",
      signTitle: "اعتماد: {{agent}} {{version}} إلى {{stage}}",
      signBody: "يُوقَّع قرارك باسمك ويُسجَّل، ولا يمكن تغييره بعد ذلك.",
      because: "السبب المذكور: {{reason}}",
      approve: "اعتماد",
      reject: "رفض",
      recorded: "سُجّل قرارك.",
      requestTitle: "طلب نقل {{agent}} {{version}} إلى {{stage}}",
      requestBodyNone: "لا تحتاج هذه الخطوة إلى اعتماد؛ تتم فورًا.",
      requestBody: "يجب أن يعتمده شخص غيرك.",
      requestReason: "لماذا الآن؟ (اختياري)",
      requestOk: "إرسال الطلب",
      requested: "أُرسل الطلب.",
      kind: { model: "نموذج ذكاء اصطناعي:", tool: "أداة:", prompt: "تعليمات:" },
      error: "تعذّر تحميل الإصدارات.",
    },
    orgRules: {
      title: "قواعد جهتك",
      intro: "قواعد تضيفها جهتك فوق القواعد المدمجة. لا يمكنها إلا أن تجعل الأمور أشد: أن ترفض شيئًا، أو تطلب اعتمادًا، أو تضع حدًا. يعتمد شخص ثانٍ كل تغيير قبل أن يسري.",
      builtinTitle: "القواعد المدمجة (مقفلة)",
      builtinIntro: "قواعد المنصة نفسها. تسري على كل الجهات ولا تتغير إلا بمراجعة من فريق المنصة.",
      add: "أضف قاعدة",
      none: "لا توجد قواعد بعد. مثال: \u201cالمدفوعات فوق 5000 تحتاج معتمدًا ماليًا\u201d.",
      whoWrites: "مسؤولو القواعد ومسؤولو الجهة يضيفون القواعد ويعتمدونها.",
      state: { draft: "مسودة", pending: "بانتظار الاعتماد", live: "مفعّلة", paused: "موقوفة مؤقتًا", retired: "متوقفة" },
      version: "الإصدار {{version}}",
      approvedBy: "اعتمدها {{who}} في {{at}}",
      waiting: "تغيير (الإصدار {{version}}) بانتظار الاعتماد، من {{who}}.",
      waitingDraft: "مسودة (الإصدار {{version}}) لم تُرسل للاعتماد بعد.",
      waitingPause: "إيقاف هذه القاعدة مؤقتًا بانتظار الاعتماد، من {{who}}.",
      waitingResume: "إعادة تفعيل هذه القاعدة بانتظار الاعتماد، من {{who}}.",
      approve: "اعتماد",
      reject: "رفض",
      withdraw: "سحب",
      submit: "أرسل للاعتماد",
      edit: "تغيير",
      pause: "إيقاف مؤقت",
      resume: "إعادة التفعيل",
      history: "السجل",
      ownChange: "أنت من أجرى هذا التغيير، لذا يعتمده شخص آخر.",
      approved: "اعتُمدت القاعدة. تسري من الآن.",
      rejected: "رُفض التغيير.",
      submitted: "أُرسلت للاعتماد.",
      withdrawn: "سُحبت.",
      saved: "حُفظت المسودة.",
      changeRequested: "أُرسل للاعتماد.",
      decisionNote: "ملاحظة (اختياري)",
      historyTitle: "سجل \u201c{{name}}\u201d",
      restore: "ارجع إلى هذا الإصدار",
      vstatus: { draft: "مسودة", pending: "بالانتظار", live: "سارية", superseded: "استُبدلت", rejected: "مرفوضة", withdrawn: "مسحوبة" },
      by: "من {{who}}",
      paused: "موقوفة مؤقتًا",
      editor: {
        titleNew: "أضف قاعدة",
        titleEdit: "غيّر \u201c{{name}}\u201d",
        step: { kind: "نوع القاعدة", details: "التفاصيل", try: "جرّبها" },
        kind: {
          who_may_use: { title: "من يمكنه الاستخدام", text: "لا يستخدم نظامًا أو أحد إجراءاته إلا الأشخاص أو الأدوار أو الوكلاء الذين تحددهم.", eg: "لا ينفذ المدفوعات إلا فريق الخزينة." },
          when_allowed: { title: "متى يُسمح", text: "نظام أو إجراء في أيام وساعات محددة فقط.", eg: "المدفوعات من الأحد إلى الخميس فقط، من 08:00 إلى 17:00." },
          needs_approval_if: { title: "يحتاج اعتمادًا إذا", text: "يجب أن يعتمد شخصٌ عندما تحقق قيمة في الطلب شرطًا.", eg: "المدفوعات فوق 5000 تحتاج معتمدًا ماليًا." },
          max_data_level: { title: "لا يتجاوز مستوى بيانات", text: "لا يتعامل الوكلاء مع بيانات فوق مستوى معين، ويمكن منعهم من نماذج الذكاء الاصطناعي عبر الإنترنت.", eg: "وكلاء الموارد البشرية لا يستخدمون النماذج عبر الإنترنت." },
          limit_per_day: { title: "حد يومي", text: "عدد أقصى من الطلبات أو مبلغ أقصى لكل شخص أو وكيل أو للجهة كلها يوميًا.", eg: "10 مدفوعات كحد أقصى لكل شخص يوميًا." },
        },
        nameEn: "الاسم (بالإنجليزية)",
        nameAr: "الاسم (بالعربية)",
        system: "النظام",
        actions: "الإجراءات",
        allActions: "كل الإجراءات",
        roles: "الأدوار",
        agents: "الوكلاء",
        allAgents: "كل الوكلاء",
        people: "الأشخاص (معرّفات المستخدمين)",
        days: "الأيام",
        from: "من",
        to: "إلى",
        zone: "المنطقة الزمنية",
        condition: "متى",
        always: "دائمًا",
        field: "قيمة في الطلب",
        op: "تكون",
        value: "من / تساوي",
        ops: { ">": "أكثر من", ">=": "على الأقل", "<": "أقل من", "<=": "على الأكثر", "==": "تساوي", "!=": "لا تساوي" },
        approvers: "يعتمدها",
        level: "أعلى مستوى للبيانات",
        online: "نماذج الذكاء الاصطناعي عبر الإنترنت",
        onlineAllowed: "مسموح",
        onlineNever: "أبدًا",
        systemOptional: "لهذا النظام فقط (اختياري)",
        per: "لكل",
        perOpt: { person: "شخص", agent: "وكيل", organisation: "الجهة كلها" },
        maxCalls: "أقصى عدد من الطلبات",
        maxAmount: "أقصى مبلغ",
        amountField: "المبلغ من",
        note: "السبب (اختياري)",
        tryIntro: "اعرف ما كانت هذه القاعدة ستفعله بطلبات آخر 7 أيام قبل أن يعتمدها أحد.",
        tryRun: "جرّبها على الأسبوع الماضي",
        tryRefused: "كانت سترفض {{count}} من {{total}} طلبًا.",
        tryApproval: "كانت ستطلب اعتمادًا في {{count}} من {{total}} طلبًا.",
        tryNone: "لم تكن لتغيّر أيًا من الطلبات الـ {{total}}.",
        tryUnknown: "تعذّر فحص {{count}} (سجلاتها أقدم من هذا النوع من القواعد).",
        tryExample: "{{effect}} · {{when}} · {{what}}",
        effect: { refused: "رُفض", approval: "اعتماد" },
        saveDraft: "احفظ المسودة",
        saveSubmit: "احفظ وأرسل للاعتماد",
      },
      text: {
        who_may_use: "لا يستخدم {{what}} إلا {{who}}.",
        when_allowed: "{{what}} فقط في {{days}}، من {{from}} إلى {{to}} ({{zone}}).",
        needs_approval_if: "{{what}} يحتاج اعتمادًا من {{roles}} عندما {{condition}}.",
        needs_approval_always: "{{what}} يحتاج دائمًا اعتمادًا من {{roles}}.",
        max_data_level: "{{agents}}: لا بيانات فوق مستوى {{level}}{{online}}.",
        noOnline: "، ولا نماذج ذكاء اصطناعي عبر الإنترنت",
        limit_per_day: "{{what}}: {{limit}} كحد أقصى لكل {{per}} يوميًا.",
        limitCalls: "{{count}} طلبات",
        limitAmount: "مبلغ {{amount}}",
        and: " و",
        what: "{{system}} ({{actions}})",
        whatAll: "{{system}}",
        everyAgent: "كل الوكلاء",
        condition: "تكون {{field}} {{op}} {{value}}",
        list: "، ",
      },
    },
    goBack: {
      button: "الرجوع إلى إصدار سابق…",
      aria: "الرجوع إلى إصدار سابق من {{agent}}",
      pickTitle: "الرجوع إلى إصدار سابق من {{agent}}",
      pickBody: "اختر المرحلة والإصدار السابق. يُطلق من جديد في تلك المرحلة، وبنفس الاعتماد المطلوب لأي إطلاق. لا يُحذف شيء: يبقى الإصدار الأحدث محفوظًا ويمكن إطلاقه لاحقًا.",
      pickLabel: "المرحلة والإصدار",
      option: "{{stage}}: الإصدار {{version}} (الحالي {{current}})",
      next: "التالي",
      title: "الرجوع إلى {{agent}} {{version}} في {{stage}}",
      explain: "يحلّ الإصدار {{version}} محل الإصدار {{from}} في {{stage}}. تستخدم الطلبات الجديدة هناك {{version}}؛ وتكتمل الطلبات الجارية على {{from}}.",
      ok: "الرجوع",
      defaultReason: "الرجوع إلى إصدار سابق",
      done: "عاد الإصدار {{version}} إلى {{stage}}.",
    },
    rules: {
      intro: "كل قاعدة تطبّقها المنصة، بكلمات بسيطة. تُفحص كل واحدة منها عند كل إجراء، قبل أن يحدث أي شيء.",
      search: "ابحث عن قاعدة",
      count_zero: "لا توجد قواعد",
      count_one: "قاعدة واحدة",
      count_two: "قاعدتان",
      count_few: "{{count}} قواعد",
      count_many: "{{count}} قاعدة",
      count_other: "{{count}} قاعدة",
      controlled: "يتحكم بها:",
      always: "مفعّلة دائمًا",
      showCode: "عرض نص السياسة",
      hideCode: "إخفاء نص السياسة",
      codeMissing: "نص السياسة غير متاح هنا.",
      codeWhat: "ما الذي يتحقق منه هذا النص",
      codeChanged: "لا يتغيّر إلا عبر مراجعة الشيفرة من مهندسي المنصة، ولا يتغيّر من هذه الشاشة أبدًا.",
      codeSettings: "الإعداد الذي يؤثر فيه:",
      codeSettingsNone: "لا يؤثر فيه أي إعداد: هو مفعّل دائمًا.",
      codeLines: "{{file}}، الأسطر {{from}}–{{to}}",
      codeWhole: "عرض الملف كاملًا",
      codePart: "عرض هذه القاعدة فقط",
      codeLabel: "نص السياسة لـ: {{rule}}",
      codeExplain: {
        four_eyes: "عندما يقرر شخص مهمة في حالة، يقارن النص بين صاحب القرار ومقدّم الطلب. فإن كانا الشخص نفسه يُرفض القرار.",
        human_decides: "يُرفض قرار مهمة الحالة أو إسنادها ما لم يقم به شخص مسجّل الدخول مباشرة. ويُرفض إن قام به وكيل أو شخص عبر وكيل.",
        task_roles: "يمكن لجهتك تحديد الأدوار المسموح لها بقرار كل نوع من المهام. ويرفض النص القرار إن لم يحمل الشخص أيًا من الأدوار المحددة لذلك النوع.",
        release_four_eyes: "عند اعتماد إصدار، يقارن النص بين المعتمِد ومن طلب الإصدار. ولا يمكن لأحد اعتماد إصداره بنفسه أبدًا.",
        live_two: "للوكلاء بمستوى خطورة 3 فأعلى، يعدّ النص الأشخاص المختلفين الذين اعتمدوا. يحتاج الانتقال إلى «مباشر» اثنين منهم، والوكلاء الأقل خطورة واحدًا.",
        eval_gate: "يُرفض الإطلاق إلى «الفحص النهائي» أو «مباشر» ما لم تنجح نتائج اختبارات الإصدار. وتأتي النتائج من «الجودة».",
        signed_release: "يُرفض الإطلاق إلى «مباشر» ما لم يحمل الإصدار توقيعًا رقميًا صالحًا يثبت أنه الإصدار نفسه الذي اختُبر.",
        approval_amount: "لكل إجراء يبحث النص عن حد المبلغ لمستوى خطورة الوكيل (إعداد المنصة، ويمكن لجهتك تشديده). فإن تجاوز مبلغ الإجراء الحد انتظر الإجراء اعتماد شخص.",
        approval_confidence: "إن ذكر الذكاء الاصطناعي مدى ثقته وكانت أقل من الحد لمستوى خطورة الوكيل، ينتظر الإجراء اعتماد شخص.",
        approval_tier: "كل إجراء يغيّر البيانات، من وكيل بمستوى الخطورة المحدد أو أعلى، يُوقف حتى يعتمده شخص.",
        untrusted: "إن كان محتوى من الخارج (بريد إلكتروني أو صفحة ويب أو مستند) جزءًا مما أدى إلى الإجراء، يُوقف الإجراء حتى يعتمده شخص.",
        always_approve: "إجراءات الأدوات الموسومة «تحتاج دائمًا إلى اعتماد» في وصف الأداة تُوقف حتى يعتمدها شخص، أيًا كان المبلغ.",
        tools_granted: "يتحقق النص من الأداة والإجراء مقابل القائمة التي أُعطيت للوكيل في «المنشئ». وكل ما ليس في القائمة يُرفض.",
        tool_roles: "بعض إجراءات الأدوات تحدد أدوارًا يجب أن يحملها مقدّم الطلب. فإن لم يحمل أيًا منها يُرفض الإجراء.",
        dry_run: "في التشغيل التجريبي يُرفض كل إجراء موسوم بأنه يغيّر البيانات، فلا تغيّر الاختبارات أنظمتك أبدًا.",
        pii_hidden: "الحقول التي تسمها الأداة بيانات شخصية تُدرج للإخفاء ما لم يحمل الشخص دور قارئ البيانات الشخصية.",
        for_person: "لا يجوز للوكيل العمل دون شخص مسجّل الدخول إلا إذا كان موسومًا بالعمل وحده (مثل العمل بجدول زمني). وإلا يُرفض الإجراء.",
        registered: "يجب أن يكون الوكيل مسجّلًا ونشطًا ومعتمدًا للمرحلة التي يعمل فيها (التطوير أو الاختبار أو الفحص النهائي أو مباشر).",
        deny_default: "يكون القرار النهائي «مسموح» فقط عندما يكون الإجراء معروفًا ولم ترفضه أي قاعدة. وكل ما عدا ذلك يُرفض.",
        clearance: "لكل بيانات مستوى (عام، داخلي، سري، مقيّد). ويُرفض الإجراء إن كان مستوى البيانات أعلى من تصريح الشخص.",
        agent_ceiling: "لكل وكيل أعلى مستوى بيانات يُحدد عند بنائه. ويُرفض الإجراء إن كان مستوى البيانات أعلى منه.",
        tenant_ceiling: "إن حددت جهتك أعلى مستوى للبيانات، يُرفض كل إجراء ببيانات أعلى منه.",
        kill: "قبل أي شيء آخر يتحقق النص من الإيقاف الطارئ للمنصة كلها ولجهتك وللوكيل. فإن كان أحدها مفعّلًا يُرفض الإجراء.",
        runner_groups: "إن كان الوكيل مقصورًا على مجموعات معينة من الأشخاص، يتحقق النص من مجموعات الشخص ويرفض الآخرين.",
        memory_consent: "تُرفض كتابة ذكرى عن شخص ما لم يوافق ذلك الشخص. والمحو مسموح دائمًا للشخص نفسه ولمسؤولي الخصوصية.",
        retrieval_rows: "تُرجع عمليات البحث في المعرفة العدد المحدد من الصفوف على الأكثر، ومن صفوف جهتك وقسمك فقط.",
        confidential_stays: "تُرفض البيانات الأعلى من حد الإنترنت (داخلي افتراضيًا) لنماذج الذكاء الاصطناعي خارج خوادمك، وتستخدم هذه الطلبات النماذج المحلية.",
        online: "تُرفض نماذج الذكاء الاصطناعي خارج خوادمك إن أوقفتها جهتك، أو إن لم يكن عنوان النموذج في القائمة المعتمدة.",
        model_approved: "يُرفض نموذج الذكاء الاصطناعي في مرحلة ما حتى يُختبر ويُعتمد لتلك المرحلة.",
        model_residency: "يُرفض نموذج الذكاء الاصطناعي خارج خوادمك إن لم تكن منطقته أو مزوّده في القائمة المسموح بها لجهتك.",
        kill_who: "يحتاج تفعيل الإيقاف الطارئ أو رفعه إلى دور مشغّل الإيقاف الطارئ أو مسؤول المنصة. وإيقاف المنصة كلها يحتاج إلى مسؤول المنصة.",
        channel_consent: "تُرفض الرسالة الصادرة دون موافقة الشخص على تلك القناة والغرض، أو إن ألغى اشتراكه، أو في ساعات الهدوء (عدا العاجلة)، أو فوق حدود الإرسال.",
        channel_handover: "يحتاج الرد على محادثة حوّلها وكيل إلى شخص مسجّل الدخول بدور موظف القناة، ويجب أن يكون التحويل مفعّلًا لجهتك.",
        users_admin: "تحتاج إدارة المستخدمين إلى دور مسؤول الجهة (أو مسؤول المنصة). ولا يجوز لأحد تغيير أدواره أو تعطيل حسابه بنفسه.",
        users_platform: "أدوار المنصة ومستخدمو فريق المنصة لا يديرها إلا مسؤول المنصة.",
        marketplace_cert: "يُرفض تثبيت مكوّن من خارج الشركة ما لم يكن معتمدًا ورخصته مفتوحة المصدر. ويحتاج الاعتماد إلى دور أمين السوق.",
        marketplace_four_eyes: "لا يمكن لمن نشر عرضًا أن يعتمده أبدًا.",
        directory: "البحث عن أسماء الأشخاص والوكلاء مقصور على جهتك.",
        agent_roles: "لا يسمح للوكيل بالعمل على الحالات بمفرده (فتحها أو تحديثها) إلا مسؤولو المؤسسة أو مسؤولو الوكلاء؛ وتبقى الموافقة والإدارة دائماً بيد الأشخاص.",
        insights: "خرائط العمليات لا يقرؤها إلا الأدوار المحددة (المنشئون والمشغّلون والمسؤولون والمدققون…). وخرائط جهة أخرى تحتاج إلى دور مدقق المنصة.",
        activity: "يرى كل شخص طلباته. وقائمة طلبات الجميع تحتاج إلى دور مشغّل أو مسؤول جهة أو مدقق، وجهة أخرى تحتاج إلى دور مدقق المنصة.",
        explain: "تُرفض قصة الطلب ما لم تكن مشاركًا فيه أو تحمل دور مراجع أو مشغّل أو مدقق أو مسؤول. والسجلات الموقّعة تحتاج إلى دور مدقق أو مسؤول.",
        agent_test: "تجربة الوكلاء مرفوضة خارج «التطوير»، ولمن لا يحمل دور المنشئ أو مسؤول الوكلاء.",
        connect_system: "ربط أنظمة الشركة يحتاج إلى دور مسؤول الأدوات أو المنصة في فريق المنصة. ويُرفض اختبار الاتصال إن كان سيغيّر البيانات.",
        platform_tools: "يُرفض ملف ينشئه وكيل بمستوى أقل من مستوى بيانات الوكيل نفسه. وتنتظر الرسالة بالبريد أو تطبيق المحادثة أو الرسائل النصية من وكيل يرى بيانات أعلى من «داخلي» موافقةً، وكذلك إغلاق وكيل لقضية.",
        org_rules: "يقرأ النص قواعد جهتك الخاصة (المنشورة بعد موافقة مسؤول ثانٍ) ويطبّق كلًّا منها فوق قواعد المنصة: يرفض الاستخدام من أي شخص لا تسمّيه القاعدة، ويرفض خارج الساعات المسموح بها، أو يطلب موافقة إضافية. ولا يمكنه إلا أن يزيد التشدد.",
      },
      none: "لا توجد قاعدة مطابقة.",
      topic: {
        people: "من يقرّر",
        approvals: "الموافقات والاعتمادات",
        data: "بياناتك",
        agents: "الوكلاء والأدوات",
        models: "نماذج الذكاء الاصطناعي",
        stop: "الإيقاف الطارئ",
        channels: "الرسائل إلى العملاء",
        admin: "الأشخاص والأدوار",
        reading: "من يرى ماذا",
      },
      link: {
        platform: "إعدادات المنصة",
        tenant: "إعدادات المؤسسة",
        users: "المستخدمون والأدوار",
        killswitch: "الإيقاف الطارئ",
        quality: "الجودة",
        builder: "المنشئ",
        channels: "القنوات",
        marketplace: "السوق",
        releases: "الإصدارات",
      },
      level: { public: "عام", internal: "داخلي", confidential: "سري", restricted: "مقيد" },
      every: "أي مبلغ",
      stoppedNone: "لا شيء متوقف حاليًا",
      stopped_zero: "لا شيء متوقف حاليًا",
      stopped_one: "إيقاف واحد مفعّل حاليًا",
      stopped_two: "إيقافان مفعّلان حاليًا",
      stopped_few: "{{count}} إيقافات مفعّلة حاليًا",
      stopped_many: "{{count}} إيقافًا مفعّلًا حاليًا",
      stopped_other: "{{count}} إيقاف مفعّل حاليًا",
      item: {
        four_eyes: "لا يمكن لمن يطلب شيئًا أن يعتمد طلبه بنفسه أبدًا.",
        human_decides: "الموافقات والقرارات في الحالات يتخذها أشخاص، لا وكيل أبدًا.",
        task_roles: "بعض أنواع المهام لا يقرّرها إلا أشخاص بدور معيّن.",
        release_four_eyes: "لا أحد يعتمد إصداره بنفسه: يجب أن يعتمده شخص غير من طلبه.",
        live_two: "قبل أن يصل وكيل عالي الخطورة إلى التشغيل الفعلي، يجب أن يعتمده شخصان مختلفان.",
        eval_gate: "لا يصل الوكيل أو التعليمات أو نموذج الذكاء الاصطناعي إلى الفحص النهائي أو التشغيل الفعلي إلا بعد اجتياز اختباراته.",
        signed_release: "لا تصل إلى التشغيل الفعلي إلا الإصدارات الموقّعة.",
        approval_amount: "المدفوعات والإجراءات الأخرى التي تتجاوز {{high}} تحتاج إلى موافقة شخص للوكلاء عالية الخطورة ({{medium}} للوكلاء متوسطة الخطورة؛ {{critical}} للوكلاء الحرجة).",
        approval_confidence: "عندما يكون الذكاء الاصطناعي واثقًا بأقل من {{confidence}}، يوافق شخص على الإجراء أولًا.",
        approval_tier: "كل إجراء يغيّر شيئًا، من وكيل بمستوى خطورة {{tier}} أو أعلى، يحتاج إلى موافقة شخص.",
        untrusted: "إذا أثّر محتوى خارجي (بريد إلكتروني أو صفحة ويب أو مستند) في إجراء، يوافق عليه شخص أولًا.",
        always_approve: "بعض إجراءات الأدوات تحتاج دائمًا إلى موافقة شخص، مهما كان المبلغ.",
        tools_granted: "لا يستخدم الوكيل إلا الأدوات التي مُنحت له، وفقط الإجراءات المحددة له.",
        tool_roles: "بعض إجراءات الأدوات تتطلب أيضًا أن يحمل الطالب دورًا معيّنًا.",
        pii_hidden: "تُخفى البيانات الشخصية في نتائج الأدوات عمّن لا يحق له رؤيتها.",
        dry_run: "التشغيل التجريبي لا يغيّر شيئًا في أنظمتك أبدًا.",
        confidential_stays: "البيانات السرية لا تغادر خوادمك أبدًا: لا يُرسل إلى نموذج ذكاء اصطناعي خارجها إلا ما يصل إلى مستوى {{online}}.",
        online_off: "نماذج الذكاء الاصطناعي خارج خوادمك معطّلة لمؤسستك.",
        online_on: "يمكن استخدام نماذج الذكاء الاصطناعي خارج خوادمك، عبر قائمة العناوين المعتمدة فقط.",
        model_approved: "لا يُستخدم نموذج الذكاء الاصطناعي في مرحلة إلا بعد اختباره واعتماده لها.",
        model_residency: "يجب أن تعمل نماذج الذكاء الاصطناعي في المناطق وتأتي من المزوّدين الذين تسمح بهم مؤسستك.",
        clearance: "لا يرى الشخص من البيانات إلا ما يصل إلى مستوى تصريحه.",
        agent_ceiling: "يعمل كل وكيل مع بيانات حتى حدّه الخاص فقط، ويُحدَّد عند بنائه.",
        tenant_ceiling: "البيانات التي تتجاوز مستوى {{level}} لا تُستخدم أبدًا في مؤسستك.",
        tenant_ceiling_none: "لا يوجد في مؤسستك حدّ إضافي للبيانات غير تصريح كل شخص وحدّ كل وكيل.",
        kill: "الإيقاف الطارئ يمنع كل إجراء للوكيل الموقوف أو لمؤسستك أو للمنصة كلها دفعة واحدة — {{state}}.",
        kill_who: "لا يستطيع الإيقاف أو إعادة التشغيل إلا مشغّلو الإيقاف الطارئ ومسؤولو المنصة؛ وإيقاف المنصة كلها يحتاج إلى مسؤول منصة.",
        for_person: "يعمل الوكلاء نيابةً عن الشخص المسجّل الذي يطلب؛ ولا يعمل دون شخص إلا الوكلاء المعلَّمون للعمل وحدهم (مثلًا وفق جدول زمني).",
        registered: "لا يعمل إلا الوكلاء المسجّلون والنشطون والمعتمدون لهذه المرحلة.",
        deny_default: "كل ما لم يُسمح به صراحةً يُرفض.",
        runner_groups: "بعض الوكلاء لا يستخدمهم إلا أشخاص في مجموعات معيّنة.",
        memory_consent: "لا يتذكر الوكيل أشياء عنك إلا بموافقتك، ويمكنك محوها في أي وقت.",
        retrieval_rows: "يعيد البحث في المعرفة {{rows}} صفًا على الأكثر في كل مرة، ومما يحق لك رؤيته فقط.",
        channel_consent: "لا تُرسل الرسائل إلا لمن وافق على تلقيها، وخارج ساعات الهدوء، وضمن حدود الإرسال.",
        channel_handover: "المحادثة التي يحيلها وكيل يرد عليها شخص بدور موظف القنوات.",
        marketplace_cert: "يجب أن تكون مكوّنات الأطراف الخارجية معتمدة وبترخيص مفتوح المصدر قبل أن يثبّتها أحد.",
        marketplace_four_eyes: "من ينشر عرضًا في السوق لا يعتمده بنفسه.",
        users_admin: "لا يدير المستخدمين والأدوار إلا مسؤولو المؤسسة، ولا يستطيع أحد تغيير أدواره أو تعطيل حسابه بنفسه.",
        users_platform: "أدوار المنصة لا يمنحها إلا مسؤول المنصة.",
        directory: "يرى كل من سجّل الدخول أسماء الأشخاص والوكلاء في مؤسسته فقط، ولا يرى مؤسسة أخرى أبدًا.",
        agent_roles: "لا يعمل الوكيل على الحالات بمفرده إلا إذا سمح بذلك مسؤول في مؤسسته، وتُسجَّل كل تغيير.",
        insights: "خرائط سير العمل لمن يبني الوكلاء أو يشغّلها أو يشرف عليها أو يدققها.",
        activity: "ترى دائمًا طلباتك أنت؛ وطلبات الجميع يراها المشغّلون ومسؤولو المؤسسة والمدققون.",
        explain: "تظهر قصة الطلب لصاحبه والمشاركين فيه والمراجعين والمشغّلين والمدققين ومسؤولي المؤسسة؛ أما السجلات الموقّعة فللمدققين والمسؤولين فقط.",
        agent_test: "تتم تجربة الوكلاء واختبارهم في مرحلة التطوير فقط، على يد المنشئين ومسؤولي الوكلاء؛ وتكون أفعالهم محاكاة ما لم يسمح المنشئ بتنفيذها فعلًا.",
        connect_system: "لا يربط أنظمة الشركة إلا مسؤولو الأدوات والمنصة؛ يقرأ الاختبار فعليًا لكنه يحاكي التغييرات فقط، وتبقى بيانات الدخول في مخزن الأسرار.",
        platform_tools: "تحتفظ الملفات التي ينشئها الوكلاء بمستوى بيانات الوكيل ولا يفتحها إلا المصرّح لهم به؛ وتنتظر موافقة شخص الرسائلُ التي تخرج من المنصة من وكلاء يرون بيانات أعلى من «داخلي»، وإغلاقُ الوكلاء للقضايا.",
        org_rules: "يمكن للجهة إضافة قواعدها الخاصة فوق قواعد المنصة، ولا تكون أخف منها أبدًا: من يستخدم الشيء، ومتى، وحجم البيانات، والنماذج عبر الإنترنت، والحدود اليومية، أو موافقة إضافية. ويحتاج أي تغيير إلى موافقة مسؤول ثانٍ.",
      },
    },
    evidence: {
      headline_zero: "لا توجد سجلات هذا الأسبوع.",
      headline_one: "سجل واحد هذا الأسبوع.",
      headline_two: "سجلان هذا الأسبوع.",
      headline_few: "{{n}} سجلات هذا الأسبوع.",
      headline_many: "{{n}} سجلًا هذا الأسبوع.",
      headline_other: "{{n}} سجل هذا الأسبوع.",
      checking: "جارٍ فحص كل سجل…",
      ok: "فُحص الكل — لم يُغيَّر أو يُحذف أي شيء.",
      broken: "تحذير: بعض السجلات غير متطابقة. ربما تم تغيير سجل التدقيق.",
      notChecked: "لم يُفحص بعد.",
      check: "الفحص من جديد",
      export: "تصدير الأدلة…",
      advanced: "بحث متقدم",
      content: {
        title: "الاحتفاظ بمحتوى المحادثات كدليل",
        help: "متوقف ما لم تشغّله جهتك. عند تشغيله تحتفظ المنصة بما أرسله كل طلب إلى الذكاء الاصطناعي وما أجاب به، وبما أُرسل إلى الأدوات وما أعادته — مع إخفاء البيانات الشخصية والتشفير بمفتاح جهتك — للأيام التي تختارها. لا يقرؤه إلا المدققون، وتُسجَّل كل قراءة، ويحذف محوُ بيانات الشخص ما يخصه.",
        off: "متوقف — لا يُحتفظ بمحتوى المحادثات",
        on: "مفعّل — يُحتفظ به {{days}} يومًا",
        changedBy: "آخر تغيير بواسطة",
        whoDecides: "يقرر ذلك المدققون ومسؤولو الجهة.",
        turnOn: "تشغيل",
        turnOff: "إيقاف",
        turnOnTitle: "الاحتفاظ بمحتوى المحادثات كدليل",
        turnOffTitle: "إيقاف الاحتفاظ بمحتوى المحادثات",
        onExplain: "من الآن يُحتفظ بمحتوى طلبات جهتك، مخفيّ البيانات الشخصية ومشفّرًا، للأيام أدناه. وما حُفظ سابقًا يبقى حتى تاريخ انتهائه.",
        offExplain: "لن يُحتفظ بالطلبات الجديدة. وما حُفظ يبقى حتى تاريخ انتهائه، ما لم يحذفه محوُ بيانات الشخص.",
        days: "مدة الاحتفاظ (أيام)",
        daysHelp: "من 1 إلى {{max}} يومًا.",
        reason: "السبب (يُسجَّل في سجل التدقيق)",
        turnedOn: "أصبح محتوى المحادثات يُحتفظ به كدليل",
        turnedOff: "لم يعد محتوى المحادثات يُحتفظ به",
      },
      licences: {
        title: "تراخيص مكونات المنصة",
        help: "كل مكوّن مفتوح المصدر بُنيت منه المنصة، مع ترخيصه وهل تسمح به سياسة التراخيص. يرفقه المدققون بالأدلة.",
        open: "عرض سجل التراخيص",
      },
      search: {
        title: "البحث عن سجلات",
        type: "ماذا حدث",
        anyType: "أي شيء",
        person: "الشخص",
        agent: "الوكيل",
        case: "الحالة",
        run: "الطلب",
        period: "متى",
        go: "بحث",
        clear: "مسح",
      },
      period: { d7: "آخر 7 أيام", d30: "آخر 30 يومًا", all: "أي وقت" },
      records: "السجلات",
      none: "لا توجد سجلات مطابقة.",
      error: "تعذّر تحميل السجلات.",
      sentence: {
        run: "بدأ <actor/> طلبًا",
        tool_call: "استخدم <actor/> {{what}}",
        policy_allow: "سمحت القواعد بـ {{what}} لـ <actor/>",
        policy_deny: "رفضت القواعد {{what}} لـ <actor/>",
        approval_requested: "طلب <actor/> موافقة شخص",
        approval_decided: "قرّر <actor/> بشأن موافقة: {{what}}",
        config_change: "غيّر <actor/> إعدادًا",
        kill_switch: "استخدم <actor/> الإيقاف الطارئ: {{what}}",
        promotion: "عمل <actor/> على إصدار: {{what}}",
        memory_erase: "محا <actor/> بيانات محفوظة",
        agent_lifecycle: "غيّر <actor/> حالة وكيل: {{what}}",
        security: "اطّلع <actor/> على سجلات حساسة",
        other: "<actor/>: {{what}}",
      },
      nobody: "المنصة",
      code: {
        case_search: "البحث في الحالات",
        case_write: "تعديل حالة",
        case_read: "قراءة حالة",
        case: "عمل على حالة",
        directory_read: "البحث عن أسماء الأشخاص",
        promotion_request: "طلب إصدار",
        promotion_approve: "اعتماد إصدار",
        killswitch_engage: "استخدام الإيقاف الطارئ",
        killswitch_release: "رفع الإيقاف الطارئ",
        memory_read: "قرأ ما يتذكره",
        memory_write: "حفظ شيئًا ليتذكره",
        memory_erase: "محا بيانات محفوظة",
        episodic: "تذكّر محادثة سابقة",
        model_call: "سأل نموذج ذكاء اصطناعي",
        ingest: "أضاف مستندات إلى مجموعة معرفة",
        approval: "طلب موافقة شخص",
        approve: "وافق",
        reject: "رفض",
        requested: "طُلب",
        approved: "اعتُمد",
        completed: "اكتمل",
        completion_failed: "لم يكتمل النقل",
        cancelled: "سُحب",
        rejected: "رُفض",
        registered: "سُجّل",
        pending_attestation: "بانتظار تأكيد هويته",
        attested: "تأكدت هويته",
        active: "يعمل",
        suspended: "متوقف مؤقتًا",
        retired: "متقاعد",
        engage: "أُوقف",
        release: "أُعيد تشغيله",
        tenant: "مؤسستك",
        global: "المنصة كلها",
        agent: "وكيل واحد",
      },
      openRequest: "فتح الطلب",
      record: "السجل {{seq}}",
    },
  },
};
